import { useContext, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { ID } from '@datassential/platform-ui-lib';
import { AppContext } from '../../../AppContext';
import useLocalStorageState from '@hooks/useLocalStorageState';
import {
    CHAIN_ENABLED_WORKSPACES,
    isWorkspaceAccessible,
    parseChainId,
    WorkspaceEnum,
} from '@models/Workspace';
import { HOME_WORKSPACE_PRESET_KEY } from "@pages/Home/sections/HomeBrandPerceptions/HomeBrandPerceptions";
import { useSetDefaultWorkspace } from '@apiHooks/useSetDefaultWorkspace';

export interface ResolutionResult {
    isReady: boolean;
    workspaceId: WorkspaceEnum | null;
    chainId: ID | null;
    chainName: string;
    setWorkspaceId: (id: WorkspaceEnum) => Promise<boolean>;
    setChainId: (id: ID | null) => void;
    isSavingWorkspace: boolean;
}

interface ResolutionOptions {
    mode: 'home' | 'backdoor';
    forcedWorkspaceId?: WorkspaceEnum;
    forcedChainId?: ID | null;
}

const PLACEHOLDER_CHAIN_KEY = '__chain_placeholder__';
const SAVE_WORKSPACE_TIMEOUT_MS = 10000;

function withTimeout(promise: Promise<void>, ms: number, onLateSuccess: () => void): Promise<void> {
    return new Promise((resolve, reject) => {
        let timedOut = false;
        const timer = setTimeout(() => {
            timedOut = true;
            reject(new Error('Timed out saving workspace selection'));
        }, ms);
        promise.then(
            (value) => {
                clearTimeout(timer);
                if (timedOut) { onLateSuccess(); return; }
                resolve(value);
            },
            (error) => { clearTimeout(timer); reject(error); },
        );
    });
}

export function useWorkspaceResolution(options: ResolutionOptions): ResolutionResult {
    const { mode, forcedWorkspaceId, forcedChainId } = options;
    const { globalVariables } = useContext(AppContext);
    const { availableWorkspaces, availableChains } = globalVariables;
    const { setDefaultWorkspace } = useSetDefaultWorkspace();

    const [overrideWorkspaceId, setOverrideWorkspaceId] = useState<WorkspaceEnum | null>(null);
    const [isSavingWorkspace, setIsSavingWorkspace] = useState(false);

    const resolvedWorkspaceId = useMemo((): WorkspaceEnum | null => {
        if (forcedWorkspaceId) return forcedWorkspaceId;

        const override = overrideWorkspaceId
            ? availableWorkspaces.find(w => w.id === overrideWorkspaceId && isWorkspaceAccessible(w))
            : null;
        if (override) return override.id;

        return availableWorkspaces.find(w => w.isDefault && isWorkspaceAccessible(w))?.id
            ?? availableWorkspaces.find(isWorkspaceAccessible)?.id
            ?? null;
    }, [forcedWorkspaceId, overrideWorkspaceId, availableWorkspaces]);

    const requiresChain = useMemo(() => (
        resolvedWorkspaceId !== null && CHAIN_ENABLED_WORKSPACES.includes(resolvedWorkspaceId)
    ), [resolvedWorkspaceId]);

    const chainKey = useMemo(() => {
        if (!requiresChain || !resolvedWorkspaceId) return PLACEHOLDER_CHAIN_KEY;
        const prefix = mode === 'home' ? 'home-chain' : 'backdoor-chain';
        return `${prefix}-${resolvedWorkspaceId}`;
    }, [mode, requiresChain, resolvedWorkspaceId]);

    const [storedChainId, setStoredChainId] = useLocalStorageState<ID | null>(
        chainKey,
        null,
        parseChainId,
    );

    const resolvedChainId = useMemo((): ID | null => {
        if (!requiresChain || availableChains.length === 0) return null;

        if (forcedChainId !== undefined) {
            return forcedChainId != null && availableChains.some(c => c.id === forcedChainId)
                ? forcedChainId
                : null;
        }

        if (storedChainId != null && availableChains.some(c => c.id === storedChainId)) {
            return storedChainId;
        }

        return availableChains.find(c => c.isDefault)?.id ?? availableChains[0]?.id ?? null;
    }, [requiresChain, forcedChainId, storedChainId, availableChains]);

    const resolvedChain = availableChains.find(c => c.id === resolvedChainId) ?? null;

    const setWorkspaceId = async (id: WorkspaceEnum): Promise<boolean> => {
        const workspace = availableWorkspaces.find(w => w.id === id);
        if (!workspace) return false;

        setIsSavingWorkspace(true);
        try {
            await withTimeout(
                setDefaultWorkspace(workspace.workspaceId),
                SAVE_WORKSPACE_TIMEOUT_MS,
                () => setOverrideWorkspaceId(id),
            );
            setOverrideWorkspaceId(id);
            return true;
        } catch {
            toast.error("Couldn't save your workspace selection. Please try again.");
            return false;
        } finally {
            setIsSavingWorkspace(false);
        }
    };

    const setChainId = (id: ID | null) => {
        setStoredChainId(id);
        localStorage.removeItem(HOME_WORKSPACE_PRESET_KEY);
    };

    const isReady = useMemo(() => {
        return resolvedWorkspaceId !== null
            && (!requiresChain || resolvedChainId !== null);
    }, [resolvedWorkspaceId, requiresChain, resolvedChainId]);

    return {
        isReady,
        workspaceId: resolvedWorkspaceId,
        chainId: resolvedChainId,
        chainName: resolvedChain?.name ?? '',
        setWorkspaceId,
        setChainId,
        isSavingWorkspace,
    };
}
